import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { Ban, UserPlus, UserCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { createUser, fetchUsers, patchUser } from '../api/users';
import type { User, UserCreate } from '../api/users';
import type { Role } from '../api/auth';
import { HeaderActions } from '../components/HeaderActions';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DataTable } from '../components/data-table/DataTable';
import { ACTIONS_COL_WIDTH } from '../components/data-table/columns';
import { RowActionButton, RowActions } from '../components/data-table/RowActions';
import { ColumnsMenu, FilterBar, FilterRow, PageHeading } from '../components/data-table/TableChrome';
import { fmtDateTime, titleCase } from '../utils/format';

const ROLES: Role[] = ['viewer', 'reviewer', 'admin'];

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers]     = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [saving, setSaving]   = useState<Record<number, boolean>>({});
  const [showDisabled, setShowDisabled] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await fetchUsers(true));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function applyLocal(id: number, patch: Partial<User>) {
    setUsers(us => us.map(u => (u.id === id ? { ...u, ...patch } : u)));
  }

  const save = useCallback(async (id: number, patch: Partial<User>, prev: User) => {
    setSaving(s => ({ ...s, [id]: true }));
    setError(null);
    try {
      await patchUser(id, patch);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setUsers(us => us.map(u => (u.id === id ? { ...u, ...prev } : u))); // revert
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  }, []);

  function changeRole(u: User, role: Role) {
    if (role === u.role) return;
    applyLocal(u.id, { role });
    void save(u.id, { role }, u);
  }

  async function toggleActive(u: User) {
    const next = !u.is_active;
    if (!next && !window.confirm(`Disable ${u.name}? They will be logged out immediately.`)) return;
    applyLocal(u.id, { is_active: next });
    await save(u.id, { is_active: next }, u);
  }

  function changeSlack(u: User, slack_handle: string) {
    const val = slack_handle.trim() || null;
    if (val === u.slack_handle) return;
    applyLocal(u.id, { slack_handle: val });
    void save(u.id, { slack_handle: val }, u);
  }

  async function handleCreate(body: UserCreate) {
    const created = await createUser(body);
    setUsers(us => [created, ...us]);
    setAddOpen(false);
  }

  const displayed = useMemo(
    () => (showDisabled ? users : users.filter(u => u.is_active)),
    [users, showDisabled],
  );

  const columns = useMemo<ColumnDef<User>[]>(() => [
    {
      id: 'name',
      size: 200,
      enableHiding: false,
      meta: { label: 'Name' },
      header: 'Name',
      cell: ({ row }) => {
        const isSelf = me?.email?.toLowerCase() === row.original.email.toLowerCase();
        return (
          <span className="whitespace-nowrap font-medium">
            {row.original.name}
            {isSelf && <span className="ml-1.5 text-[11px] text-muted-foreground">(you)</span>}
          </span>
        );
      },
    },
    {
      id: 'email',
      meta: { label: 'Email' },
      header: 'Email',
      cell: ({ row }) => <span className="whitespace-nowrap text-zinc-700">{row.original.email}</span>,
    },
    {
      id: 'role',
      meta: { label: 'Role' },
      header: 'Role',
      cell: ({ row }) => {
        const u = row.original;
        const isSelf = me?.email?.toLowerCase() === u.email.toLowerCase();
        return (
          <Select
            value={u.role}
            disabled={isSelf || !!saving[u.id]}
            onValueChange={v => changeRole(u, v as Role)}
          >
            <SelectTrigger
              className="w-[112px]"
              title={isSelf ? 'You cannot change your own role' : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map(r => (
                <SelectItem key={r} value={r}>{titleCase(r)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      id: 'slack',
      meta: { label: 'Slack' },
      header: 'Slack',
      cell: ({ row }) => (
        <SlackCell
          value={row.original.slack_handle ?? ''}
          disabled={!!saving[row.original.id]}
          onCommit={val => changeSlack(row.original, val)}
        />
      ),
    },
    {
      id: 'lastLogin',
      meta: { label: 'Last login' },
      header: 'Last login',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{fmtDateTime(row.original.last_login_at)}</span>
      ),
    },
    {
      id: 'status',
      meta: { label: 'Status' },
      header: 'Status',
      cell: ({ row }) =>
        row.original.is_active ? (
          <Badge variant="success" dot="bg-green-500">Active</Badge>
        ) : (
          <Badge variant="neutral" dot="bg-zinc-400">Disabled</Badge>
        ),
    },
    {
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const u = row.original;
        const isSelf = me?.email?.toLowerCase() === u.email.toLowerCase();
        return (
          <RowActions>
            <RowActionButton
              label={isSelf ? 'You cannot disable yourself' : u.is_active ? 'Disable user' : 'Re-enable user'}
              icon={u.is_active ? Ban : UserCheck}
              tone={u.is_active ? 'default' : 'success'}
              disabled={isSelf || !!saving[u.id]}
              onClick={() => void toggleActive(u)}
            />
          </RowActions>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [me?.email, saving, save]);

  const table = useReactTable({
    data: displayed,
    columns,
    getRowId: row => String(row.id),
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    initialState: { columnPinning: { left: ['name'], right: ['actions'] } },
  });

  return (
    <>
      <HeaderActions>
        <Button onClick={() => setAddOpen(true)}>
          <UserPlus className="h-3.5 w-3.5" />
          Add user
        </Button>
      </HeaderActions>

      <PageHeading title="Users" count={displayed.length} />

      <FilterBar>
        <FilterRow>
          <label className="flex cursor-pointer items-center gap-2 text-[13px] text-muted-foreground">
            <Checkbox
              checked={showDisabled}
              onCheckedChange={v => setShowDisabled(!!v)}
              aria-label="Show disabled users"
            />
            Show disabled users
          </label>
          <div className="ml-auto">
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {/* mb-5: this page has no pagination bar, so the table is the last thing
          in the column and `main` no longer pads the bottom. */}
      <DataTable table={table} loading={loading} emptyMessage="No users found" className="mb-5" />

      {addOpen && <AddUserModal onClose={() => setAddOpen(false)} onCreate={handleCreate} />}
    </>
  );
}

// ── Slack inline edit ──────────────────────────────────────────────────────────

function SlackCell({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled?: boolean;
  onCommit: (val: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  function commit() {
    setEditing(false);
    if (draft !== value) onCommit(draft);
  }

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        className="rounded-sm px-1 py-0.5 text-left hover:bg-accent disabled:cursor-not-allowed"
      >
        {value || <span className="text-zinc-300">—</span>}
      </button>
    );
  }

  return (
    <Input
      autoFocus
      type="text"
      value={draft}
      placeholder="@handle"
      className="h-7 w-32"
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
    />
  );
}

// ── Add user modal ───────────────────────────────────────────────────────────

function AddUserModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (body: UserCreate) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [role, setRole]   = useState<Role>('reviewer');
  const [slack, setSlack] = useState('');
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await onCreate({
        email: email.trim(),
        name: name.trim(),
        role,
        slack_handle: slack.trim() || null,
      });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Create failed');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4" onClick={onClose}>
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-sm rounded-lg border border-border bg-background p-5 shadow-xl"
      >
        <h2 className="mb-4 text-base font-semibold">Add user</h2>

        {err && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>
        )}

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Email</span>
          <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="person@ivn.pl" />
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Name</span>
          <Input type="text" required value={name} onChange={e => setName(e.target.value)} />
        </label>

        <div className="mb-3 flex gap-3">
          <label className="flex-1">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Role</span>
            <Select value={role} onValueChange={v => setRole(v as Role)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map(r => (
                  <SelectItem key={r} value={r}>{titleCase(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex-1">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Slack (optional)</span>
            <Input type="text" value={slack} onChange={e => setSlack(e.target.value)} placeholder="@handle" />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add user'}</Button>
        </div>
      </form>
    </div>
  );
}
